;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import { analytics } from '@/lib/analytics/posthog'

import {
  bing,
  conversionValue,
  eventNames,
  google,
  linkedin,
  meta,
  pixelsEnabled,
  tiktok,
  type ConversionType,
} from '@/config/analytics'
import { normalizeEmail, normalizePhone } from '@/lib/analytics/identity'

type Gtag = (command: string, action: string, params?: Record<string, unknown>) => void
type Uet = { push: (...args: unknown[]) => void }
type Ttq = {
  identify: (data: Record<string, unknown>) => void
  track: (event: string, params?: Record<string, unknown>, options?: Record<string, unknown>) => void
  page: () => void
}
type Lintrk = (action: string, params: Record<string, unknown>) => void

declare global {
  interface Window {
    fbq?: (command: string, target: string, params?: Record<string, unknown>, options?: Record<string, unknown>) => void
    gtag?: Gtag
    uetq?: Uet | unknown[]
    ttq?: Ttq
    lintrk?: Lintrk
  }
}

const readCookie = (name: string): string | undefined => {
  if (typeof document === 'undefined') {
    return undefined
  }
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : undefined
}

/**
 * Copies the ad platforms' first-party cookies onto the PostHog person, so the server-side
 * Conversions APIs can send the real values.
 *
 * **Why the person and not the event.** Three conversions have no browser behind them — the
 * Calendly booking and Mollie's two webhooks — so they carry no cookies of their own. The Manager
 * reads these same cookies from any request that does have them (`App\Support\RequestData`) and
 * writes them to the person, but somebody whose *first* conversion is a booked demo never made
 * such a request. Without this, that visitor reaches the ad platforms with no click id at all.
 *
 * This used to cover Meta only, which left exactly that hole open for TikTok and LinkedIn. Google
 * and Microsoft are not here because their click ids arrive as URL parameters rather than cookies;
 * the Manager reads those from the request and PostHog records them as `$initial_*` besides.
 *
 * Safe to call repeatedly, and only ever sets what is present — an absent cookie must not
 * overwrite a value an earlier visit established.
 *
 * Module-private: `firePixelConversion` below is the only caller, and a conversion is the only
 * moment worth paying for this. It was exported for no reason other than habit.
 */
function syncAdMatchKeys(): void {
  const cookies: Record<string, string | undefined> = {
    // Meta. `fbc` in particular: PostHog otherwise rebuilds it from `fbclid` with a
    // now() timestamp, which is a different value from the one the click actually created.
    fbc: readCookie('_fbc'),
    fbp: readCookie('_fbp'),
    // TikTok's and LinkedIn's equivalents.
    ttp: readCookie('_ttp'),
    li_fat_id: readCookie('li_fat_id'),
  }

  const present = Object.fromEntries(Object.entries(cookies).filter(([, value]) => Boolean(value)))

  if (Object.keys(present).length > 0) {
    analytics.setPersonProperties(present)
  }
}

/**
 * Fires the browser pixel PageView on client-side route changes, from `PixelPageviews`. The initial
 * pageview of a load is fired by each pixel's own bootstrap snippet, which is what those snippets
 * are for and the only thing that runs reliably before hydration. Bing UET tracks SPA navigation
 * itself and is absent here.
 *
 * **Deliberately not held until the pixel is up**, unlike every other fire in this module. A route
 * change that beats a pixel's snippet is followed by that snippet's own bootstrap PageView, which
 * records whatever URL is current when it runs — the new route. A held PageView would then land on
 * top of it and count the same page twice, while the page it was meant for is gone. Skipping it
 * loses nothing: the bootstrap covers the page the visitor is on.
 *
 * No event id: nothing is routed from `$pageview` to a server-side destination, so there is nothing
 * to deduplicate against. See the note in `ad-pixels.tsx` for why attaching PostHog's uuid was
 * removed rather than fixed.
 */
export function firePixelPageview(): void {
  if (typeof window === 'undefined' || !pixelsEnabled) {
    return
  }
  if (meta.pixelId && window.fbq) {
    window.fbq('track', 'PageView')
  }
  if (tiktok.pixelId && window.ttq) {
    window.ttq.page()
  }
  if (google.adsId && window.gtag) {
    window.gtag('event', 'page_view', { send_to: google.adsId })
  }
}

/**
 * Fires the browser-pixel `ViewContent` for a page that signals intent without being a conversion —
 * the pricing page. Meta and TikTok both define the standard event; Google Ads, Microsoft and
 * LinkedIn have no equivalent and are left alone.
 *
 * **Why it exists.** A Meta ad set optimising on `StartTrial` sees one to four events a week and
 * never leaves its learning phase, so delivery is a guess. `ViewContent` on the pricing page happens
 * sixty to eighty times a week and is the earliest signal that separates someone comparing prices
 * from someone who tapped a Reel by accident. That is what the retargeting ad set optimises on.
 *
 * `eventId` is the dedup key shared with the server twin that `PixelViewContent` posts to
 * `/api/view-content` — Meta's `eventID`, TikTok's `event_id` — the same mechanism the four
 * conversions use with the Manager's id. TikTok gets its own parameter shape: its `ViewContent`
 * identifies content through `contents[]`, not Meta's `content_name`.
 *
 * No-op unless pixels are enabled.
 */
export function firePixelViewContent(contentName: string, eventId: string): void {
  if (typeof window === 'undefined' || !pixelsEnabled) {
    return
  }

  if (meta.pixelId) {
    whenPixelReady(
      () => window.fbq,
      (fbq) => fbq('track', 'ViewContent', { content_name: contentName }, { eventID: eventId }),
    )
  }
  if (tiktok.pixelId) {
    whenPixelReady(
      () => window.ttq,
      (ttq) =>
        ttq.track(
          'ViewContent',
          { contents: [{ content_id: contentName, content_type: 'product', content_name: contentName }] },
          { event_id: eventId },
        ),
    )
  }
}

/**
 * Runs `fn` once a pixel's global exists on the page, and immediately when it already does.
 *
 * Every pixel here is injected by `next/script` after hydration — Meta and Google `afterInteractive`,
 * Bing, TikTok and LinkedIn `lazyOnload`, which is after the window's `load` event and an idle
 * callback. A React effect or a form handler can therefore run before the global it wants to call
 * exists, and a fire guarded on `window.x` alone was silently dropped in that window. For TikTok on
 * a cold load that was every ad landing: the one visit `ViewContent` is for. Meta only escaped it
 * because `<AdPixels />` happens to render before the page tree.
 *
 * Polling rather than script `onLoad` hooks: the fire sites do not own the `<Script>` elements, and a
 * bounded poll has no ordering to get wrong. A visitor who leaves inside twenty seconds, or a
 * browser that blocks the pixel, simply never fires — the outcome the old guard produced every time.
 *
 * Not used for route-change pageviews; see `firePixelPageview` for why those must not be held.
 */
function whenPixelReady<T>(read: () => T | undefined | null, fn: (api: T) => void, attempt = 0): void {
  const api = read()
  if (api) {
    fn(api)
    return
  }
  if (attempt >= PIXEL_READY_ATTEMPTS) {
    return
  }
  window.setTimeout(() => whenPixelReady(read, fn, attempt + 1), PIXEL_READY_INTERVAL_MS)
}

const PIXEL_READY_INTERVAL_MS = 250
const PIXEL_READY_ATTEMPTS = 80

type FireArgs = {
  /** Shared dedup id — the backend-provided eventId. Used as each platform's event/transaction id. */
  eventId?: string
  email?: string
  phone?: string
  firstName?: string
  lastName?: string
  /** The person's stable id: the lead's uuid the Manager returns, the `externalId` its server conversion carries. */
  externalId?: string
  value?: number
  currency?: string
  /**
   * The Manager's `repeat`: this person submitted the same conversion less than 90 days ago, so it
   * is not a new one and the Manager sent nothing (workspace ADR 0015). Absent reads as `false`.
   */
  repeat?: boolean
}

const clean = (value?: string): string | undefined => {
  const trimmed = value?.trim()
  return trimmed ? trimmed : undefined
}

/**
 * Fires the browser pixel for a conversion on every configured platform with the fullest match
 * data available (email, phone, name, external_id) for advanced/enhanced matching, using the
 * shared `eventId` as the dedup key so each platform counts it once against the server-side CAPI.
 * No-op unless pixels are enabled (production, or the force override).
 *
 * Each platform's calls are held until its global exists (`whenPixelReady`), so a conversion on a
 * slow connection — or a fast visitor — is not lost to a pixel that has not loaded yet. The
 * server-side twin covers that case regardless; this keeps the browser half, and with it the
 * pixel's own cookies, in the pair Meta and TikTok deduplicate.
 *
 * A **repeat** fires nothing: Meta deduplicates a browser event against a server event only within
 * 48 hours of the first, so the pixel alone would count a second conversion the Manager deliberately
 * did not send.
 */
export function firePixelConversion(type: ConversionType, args: FireArgs): void {
  if (typeof window === 'undefined' || !pixelsEnabled) {
    return
  }
  const { eventId, externalId, value, currency = 'EUR' } = args
  const email = args.email ? normalizeEmail(args.email) : undefined
  const phone = normalizePhone(args.phone)
  const firstName = clean(args.firstName)
  const lastName = clean(args.lastName)
  const amount = value ?? conversionValue[type]
  const names = eventNames[type]

  // Make the real cookies available to the server-side Meta CAPI as soon as we have a conversion.
  syncAdMatchKeys()

  if (args.repeat === true) {
    return
  }

  // Meta — advanced matching (the pixel hashes these client-side).
  if (meta.pixelId) {
    const pixelId = meta.pixelId
    whenPixelReady(
      () => window.fbq,
      (fbq) => {
        const userData = {
          ...(email ? { em: email } : {}),
          ...(phone ? { ph: phone } : {}),
          ...(firstName ? { fn: firstName } : {}),
          ...(lastName ? { ln: lastName } : {}),
          ...(externalId ? { external_id: externalId } : {}),
        }
        if (Object.keys(userData).length > 0) {
          fbq('init', pixelId, userData)
        }
        fbq('track', names.meta, { value: amount, currency }, eventId ? { eventID: eventId } : undefined)
      },
    )
  }

  // Google Ads — enhanced conversions user data.
  if (google.adsId && google.labels[type]) {
    const sendTo = `${google.adsId}/${google.labels[type]}`
    whenPixelReady(
      () => window.gtag,
      (gtag) => {
        if (email || phone || firstName || lastName) {
          gtag('set', 'user_data', {
            ...(email ? { email } : {}),
            ...(phone ? { phone_number: phone } : {}),
            ...(firstName || lastName
              ? {
                  address: {
                    ...(firstName ? { first_name: firstName } : {}),
                    ...(lastName ? { last_name: lastName } : {}),
                  },
                }
              : {}),
          })
        }
        gtag('event', 'conversion', {
          send_to: sendTo,
          value: amount,
          currency,
          transaction_id: eventId, // Google dedup key
        })
      },
    )
  }

  // Microsoft / Bing UET — enhanced conversions PII + conversion goal. `uetq` is a plain array until
  // the tag bootstraps, and the bootstrap replays it, so pushing onto the array is the platform's
  // own queue; the wait is only for the snippet that creates the array.
  if (bing.uetTagId) {
    whenPixelReady(
      () => window.uetq,
      (uetq) => {
        if (!Array.isArray(uetq) && 'push' in uetq) {
          if (email || phone) {
            uetq.push('set', { pid: { ...(email ? { em: email } : {}), ...(phone ? { ph: phone } : {}) } })
          }
          uetq.push('event', names.bing, { revenue_value: amount, currency, transaction_id: eventId })
        } else if (Array.isArray(uetq)) {
          // UET not initialised yet — queue the event; PII pid is best-effort once loaded.
          ;(uetq as unknown[]).push('event', names.bing, { revenue_value: amount, currency, transaction_id: eventId })
        }
      },
    )
  }

  // TikTok — identify hashes the user data client-side.
  if (tiktok.pixelId) {
    whenPixelReady(
      () => window.ttq,
      (ttq) => {
        if (email || phone || externalId) {
          ttq.identify({
            ...(email ? { email } : {}),
            ...(phone ? { phone_number: phone } : {}),
            ...(externalId ? { external_id: externalId } : {}),
          })
        }
        ttq.track(names.tiktok, { value: amount, currency }, eventId ? { event_id: eventId } : undefined)
      },
    )
  }

  // LinkedIn — the Insight Tag matches on its li_fat_id cookie; user data goes via the server CAPI.
  const linkedinConversion = linkedin.conversions[type]
  if (linkedin.partnerId && linkedinConversion) {
    whenPixelReady(
      () => window.lintrk,
      (lintrk) => lintrk('track', { conversion_id: Number(linkedinConversion) }),
    )
  }
}
